import { graphqlFetch } from '@/utils/graphqlFetch';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MutationValidateSchoolProposalArgs,
  SchoolOutput,
  SchoolProposalsQuery,
  SchoolQuery,
  ValidateSchoolProposal,
} from '@/api/gql/graphql';
import {
  GET_SCHOOLS,
  GET_SCHOOLS_PROPOSALS,
  GET_SCHOOLS_SIMPLE,
  GET_SCHOOLS_STATISTICS,
} from '../queries/school';
import { SchoolsStatsOutput } from '../gql/graphql';
import { VALIDATE_SCHOOL_PROPOSAL } from '../mutations/school';

export const useGetSchools = (query: SchoolQuery) => {
  return useQuery<SchoolOutput[], Error>({
    queryKey: ['schools', query],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getSchoolsWithUsers: SchoolOutput[] },
        { query: SchoolQuery }
      >(GET_SCHOOLS, { query });
      return data.getSchoolsWithUsers;
    },
  });
};
export const useGetSchoolsProposals = (queries: SchoolProposalsQuery) => {
  return useQuery<SchoolOutput[], Error>({
    queryKey: ['schoolsProposal', queries],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getSchoolProposals: SchoolOutput[] },
        { queries: SchoolProposalsQuery }
      >(GET_SCHOOLS_PROPOSALS, { queries });
      return data.getSchoolProposals;
    },
  });
};

export const useGetSchoolsStatistics = () => {
  return useQuery<SchoolsStatsOutput, Error>({
    queryKey: ['schoolsStatistics'],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getSchoolsStats: SchoolsStatsOutput },
        { queries: SchoolsStatsOutput }
      >(GET_SCHOOLS_STATISTICS);
      return data.getSchoolsStats;
    },
  });
};

export const useGetSchoolsSimple = (query: SchoolQuery) => {
  return useQuery<SchoolOutput[], Error>({
    queryKey: ['schoolSimilar', query],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getSchools: SchoolOutput[] },
        { query: SchoolQuery }
      >(GET_SCHOOLS_SIMPLE, { query });
      return data.getSchools || [];
    },
    enabled: !!query.search,
  });
};

export const useValidateSchoolProposal = () => {
  const queryClient = useQueryClient();
  return useMutation<ValidateSchoolProposal, Error, ValidateSchoolProposal>({
    mutationFn: async (input: ValidateSchoolProposal) => {
      const data = await graphqlFetch<
        {
          validateSchoolProposal: ValidateSchoolProposal;
        },
        MutationValidateSchoolProposalArgs
      >(VALIDATE_SCHOOL_PROPOSAL, { input });
      return data.validateSchoolProposal;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['schoolsProposal'],
      }),
  });
};
